import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
    AppLayout,
    Box,
    SpaceBetween,
    Header,
    Button,
    Table,
    Modal,
    ColumnLayout
} from "@amzn/awsui-components-react";


import SideNav from "./SideNav";


import { CodeView } from '@amzn/awsui-code-view';
import jsonHighlight from "@amzn/awsui-code-view/highlight/json";



import moment from 'moment';


import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import { convertEpochToPST } from "../Frisco/FilterUtilities";


const AuditLoggingMain = (props) => {
    const [sideHeader, setSideHeader] = useState();
    const [headerHref, setHeaderHref] = useState();
    const [sideNavBack, setSideNavBack] = useState();
    const [visible, setVisible] = useState(false)
    const [before, setBefore] = useState("")
    const [after, setAfter] = useState("")
    const location = useLocation();
    const history = useHistory();
    const autoOpenedTimestamp = useRef(null);
    const queryParams = new URLSearchParams(location.search);
    const requestedChangeTimestamp =
        props.changeTimestamp ||
        props.timestamp ||
        queryParams.get("changeTimestamp") ||
        queryParams.get("timestamp");

    useEffect(() => {
        setHeaderHref(props.headerHref);
        setSideNavBack(props.sideNavBack);
        setSideHeader(props.sideHeader);

    }, [props.headerHref, props.sideNavBack, props.sideHeader])

    const sortJsonKeys = (value) => {
        if (Array.isArray(value)) return value.map(sortJsonKeys);
        if (value && typeof value === "object") {
            return Object.keys(value)
                .sort((left, right) => left.localeCompare(right))
                .reduce((sorted, key) => {
                    sorted[key] = sortJsonKeys(value[key]);
                    return sorted;
                }, {});
        }
        return value;
    };

    const formatJson = (value) => {
        if (value == null || value === "") return "Not Available";
        try {
            const parsed = typeof value === "string" ? JSON.parse(value) : value;
            return JSON.stringify(sortJsonKeys(parsed), null, 2);
        } catch (error) {
            return String(value);
        }
    };

    const copyJson = async (value) => {
        if (!navigator.clipboard || value === "Not Available") return;
        await navigator.clipboard.writeText(value);
    };

    const getLogTimestamp = (log) => {
        const itemTimestamp = String(log?.last_updated_time_stamp ?? "");
        return itemTimestamp || String(log?.sk ?? "").split("#").pop();
    };

    const setTimestampInUrl = (timestamp) => {
        if (!timestamp) return;
        const params = new URLSearchParams(location.search);
        if (params.get("changeTimestamp") === timestamp && !params.has("timestamp")) return;
        params.set("changeTimestamp", timestamp);
        params.delete("timestamp");
        history.replace({
            pathname: location.pathname,
            search: `?${params.toString()}`,
            hash: location.hash,
        });
    };

    const viewChangesClick = (e) => {
        const timestamp = getLogTimestamp(e);
        autoOpenedTimestamp.current = timestamp || null;
        setTimestampInUrl(timestamp);
        setBefore(formatJson(e.before))
        setAfter(formatJson(e.after))
        setVisible(true)
    }

    const closeChangesModal = () => {
        setVisible(false)
        setAfter("")
        setBefore("")
        autoOpenedTimestamp.current = null;

        const params = new URLSearchParams(location.search);
        const hadTimestamp = params.has("changeTimestamp") || params.has("timestamp");
        params.delete("changeTimestamp");
        params.delete("timestamp");
        if (hadTimestamp) {
            const search = params.toString();
            history.replace({
                pathname: location.pathname,
                search: search ? `?${search}` : "",
                hash: location.hash,
            });
        }
    };

    useEffect(() => {
        if (!requestedChangeTimestamp || !Array.isArray(props.logsData)) return;
        const timestamp = String(requestedChangeTimestamp);
        if (autoOpenedTimestamp.current === timestamp) return;

        const matchingLog = props.logsData.find((log) => {
            const itemTimestamp = String(log?.last_updated_time_stamp ?? "");
            const skTimestamp = String(log?.sk ?? "").split("#").pop();
            return itemTimestamp === timestamp || skTimestamp === timestamp;
        });
        if (!matchingLog) return;

        autoOpenedTimestamp.current = timestamp;
        viewChangesClick(matchingLog);
        // formatJson/viewChangesClick are local formatting helpers; reopening is
        // intentionally controlled only by log data and the requested timestamp.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.logsData, requestedChangeTimestamp]);

    const content = (
        <>
            <Table
                columnDefinitions={[
                    {
                        id: "timeStamp",
                        header: "Time Stamp",
                        cell: e => convertEpochToPST(Number(e.last_updated_time_stamp)),
                        sortingField: "last_updated_time_stamp"
                    },
                    {
                        id: "action",
                        header: "Action",
                        cell: e => e.action,
                        isRowHeader: true
                    },
                    {
                        id: "actor",
                        header: "Actor",
                        cell: e => e.actor
                    },
                    { id: "comments", header: "Comments", cell: e => e.comments },
                    { id: "before_after", header: "Before / After", cell: e => (e.before || e.after) ? <Box textAlign="left"><Button onClick={() => viewChangesClick(e)}>View Changes</Button></Box> : <Box textAlign="left">Not Applicable</Box> }
                    
                ]}
                enableKeyboardNavigation
                sortingDescending
                resizableColumns
                stripedRows
                items={props.logsData}
                loading={!props.logsData}
                loadingText="Loading logs"
                trackBy="name"
                empty={
                    <Box
                        margin={{ vertical: "xs" }}
                        textAlign="center"
                        color="inherit"
                    >
                        <SpaceBetween size="m">
                            <b>No logs available</b>
                        </SpaceBetween>
                    </Box>
                }
                header={<Header>{props.sideHeader}</Header>}
            />
            <Modal
                onDismiss={closeChangesModal}
                visible={visible}
                size="max"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="primary" onClick={closeChangesModal}>
                                Ok
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
                header="Before / After"
            >
                    <ColumnLayout columns={2}>
                    <Box>
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            <Box variant="h4">Before:</Box>
                            <Button
                                variant="icon"
                                iconName="copy"
                                ariaLabel="Copy before JSON"
                                disabled={before === "Not Available"}
                                onClick={() => copyJson(before)}
                            />
                        </SpaceBetween>
                        <CodeView content={before}
                            highlight={before && (jsonHighlight)}

                        />
                    </Box>
                    <Box>
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            <Box variant="h4">After:</Box>
                            <Button
                                variant="icon"
                                iconName="copy"
                                ariaLabel="Copy after JSON"
                                disabled={after === "Not Available"}
                                onClick={() => copyJson(after)}
                            />
                        </SpaceBetween>
                        <CodeView content={after}
                            highlight={after && (jsonHighlight)}
                        />
                    </Box>
                    </ColumnLayout>

            </Modal>
        </>
    );


    return (
        <AppLayout
            content={content}
            headerSelector="#TopBar"
            navigation={ <SideNav sideHeader={sideHeader} back={sideNavBack} headerHref={headerHref} /> }
            navigationHide={props.disableAppLayout}
            maxContentWidth={Number.MAX_VALUE}
            headerVariant={ "high-contrast"}
        ></AppLayout>
    );
};

export default AuditLoggingMain;
